import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

const IDLE_LIMIT_MS = 15 * 60 * 1000;   // 15 minutes total
const WARNING_MS    = 60 * 1000;         // show modal in the last 60s
const ACTIVITY_KEY  = 'roofmate_last_activity';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel'];

/**
 * Auto-signs the user out after IDLE_LIMIT_MS of no input.
 * - Listens to mouse / keyboard / touch / scroll activity
 * - Shows a countdown warning modal 60s before logout
 * - Syncs activity across browser tabs via localStorage
 *
 * Important UX rule: once the warning dialog is on screen, ONLY the explicit
 * "Stay signed in" or "Sign out now" buttons should dismiss it. Random mouse
 * movement, scrolling, hitting Escape, or clicking outside the dialog must
 * NOT auto-reset the timer or close the modal — otherwise the security prompt
 * is effectively bypassed by the user simply moving their cursor into the
 * page. The warning is a deliberate confirmation step.
 */
export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_MS / 1000));

  const lastActivityRef = useRef(Date.now());
  const tickRef = useRef(null);
  const loggedOutRef = useRef(false);
  // Mirror of showWarning that's readable synchronously inside event listeners
  // (those callbacks are wired up once and would otherwise close over a stale
  // showWarning from when the effect ran).
  const warningOpenRef = useRef(false);

  useEffect(() => { warningOpenRef.current = showWarning; }, [showWarning]);

  const writeActivity = useCallback((ts) => {
    lastActivityRef.current = ts;
    try { localStorage.setItem(ACTIVITY_KEY, String(ts)); } catch (_) { /* storage may be blocked */ }
  }, []);

  const doLogout = useCallback(async () => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    setShowWarning(false);
    toast.info('You were signed out due to 15 minutes of inactivity.');
    try { await logout(); } catch (_) { /* logout best-effort */ }
  }, [logout]);

  // Explicit "Stay signed in" — the ONLY UI path that closes the dialog
  // without logging the user out.
  const stayActive = useCallback(() => {
    writeActivity(Date.now());
    setShowWarning(false);
  }, [writeActivity]);

  // Bind activity listeners only while authenticated
  useEffect(() => {
    if (!user) return undefined;

    loggedOutRef.current = false;
    writeActivity(Date.now());

    // Activity events count ONLY while the warning is not open. Once the
    // warning is showing, we freeze the idle timer and wait for an explicit
    // user choice — otherwise the dialog closes the instant the user moves
    // their mouse into the page, defeating the purpose of the security prompt.
    const onActivity = () => {
      if (loggedOutRef.current) return;
      if (warningOpenRef.current) return;
      writeActivity(Date.now());
    };
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );

    // Cross-tab sync: another tab's activity counts as activity here, but
    // ONLY if the warning isn't currently open on this tab — same rationale.
    const onStorage = (e) => {
      if (e.key !== ACTIVITY_KEY || !e.newValue) return;
      if (warningOpenRef.current) return;
      const ts = parseInt(e.newValue, 10);
      if (!Number.isNaN(ts) && ts > lastActivityRef.current) {
        lastActivityRef.current = ts;
      }
    };
    window.addEventListener('storage', onStorage);

    // Polling loop (1s) — cheap and resilient to tab throttling vs. a single long setTimeout.
    tickRef.current = setInterval(() => {
      if (loggedOutRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_LIMIT_MS) {
        doLogout();
        return;
      }
      const remaining = IDLE_LIMIT_MS - elapsed;
      if (remaining <= WARNING_MS) {
        setSecondsLeft(Math.max(1, Math.ceil(remaining / 1000)));
        setShowWarning(true);
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      window.removeEventListener('storage', onStorage);
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [user, writeActivity, doLogout]);

  if (!user) return null;

  return (
    <Dialog
      open={showWarning}
      // The dialog must NOT dismiss itself on outside-click / Escape — it's a
      // security prompt, not a regular modal. Suppress any close attempt that
      // isn't the explicit Stay-signed-in or Sign-out-now button.
      onOpenChange={() => { /* no-op: only buttons can change open state */ }}
    >
      <DialogContent
        data-testid="idle-warning-dialog"
        className="sm:max-w-md"
        // Radix dialog primitives fire these for ESC / pointer outside —
        // preventDefault keeps the dialog open in both cases.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle data-testid="idle-warning-title">Still there?</DialogTitle>
          <DialogDescription data-testid="idle-warning-description">
            You&apos;ve been inactive for a while. For your security, you&apos;ll be
            signed out in{' '}
            <span className="font-semibold text-foreground" data-testid="idle-warning-countdown">
              {secondsLeft}s
            </span>
            .
          </DialogDescription>
        </DialogHeader>
        {/*
          Visual countdown bar — drains right-to-left from 100% to 0% over the
          warning window. Track width is bound directly to `secondsLeft` so the
          1-second polling tick drives a smooth one-step-per-second shrink
          (CSS transition smooths each step). Colour shifts amber -> red as
          time runs out so the urgency reads at a glance.
        */}
        <div
          className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
          role="progressbar"
          aria-label="Time remaining before automatic sign-out"
          aria-valuemin={0}
          aria-valuemax={Math.floor(WARNING_MS / 1000)}
          aria-valuenow={secondsLeft}
          data-testid="idle-warning-progress"
        >
          <div
            className={
              'h-full rounded-full transition-[width,background-color] duration-1000 ease-linear ' +
              (secondsLeft <= 10 ? 'bg-red-500' : secondsLeft <= 30 ? 'bg-orange-500' : 'bg-amber-400')
            }
            style={{ width: `${Math.max(0, Math.min(100, (secondsLeft / (WARNING_MS / 1000)) * 100))}%` }}
            data-testid="idle-warning-progress-fill"
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={doLogout}
            data-testid="idle-logout-now-btn"
          >
            Sign out now
          </Button>
          <Button
            onClick={stayActive}
            data-testid="idle-stay-signed-in-btn"
          >
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
